<template>
  <div>
    <div class="hints">
      <ul>
        <li>
          Merci d'ajouter l'adresse exacte des biens à visiter et les
          coordonnées de l'agence ou du locataire en place qui effectue les
          visites
        </li>
        <li>
          Une fois le planning réalisé, envoyer le récapitulatif en pdf
          téléchargeable par email au client, idéalement 2 jours avant la date
          des visites
        </li>
        <li>
          Leur demander si ils sont disponibles pour effectuer les visites, si
          il souhaite être accompagné dans les visites. et si il n'est pas
          disponible, lui faire une signer une lettre nous autorisant à faire la
          visite pour lui?
        </li>
      </ul>
    </div>
    <v-card-title class="align-end">
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <div tooltip="Voir les propositions abandonnées">
        <v-icon
          class="mr-3"
          :color="showDisabled ? 'primary' : 'grey'"
          @click="showDisabled = !showDisabled"
          >visibility</v-icon
        >
      </div>
      <v-icon color="#FF7C5D" @click="exportPDF">picture_as_pdf</v-icon>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="proposals"
      :custom-sort="customSort"
      class="proposals-table"
      :pagination.sync="pagination"
      hide-actions
    >
      <template v-slot:items="props">
        <ProjectPropositionsRow
          :item="props.item"
          mode="visit"
          v-model="props.expanded"
        />
      </template>
      <template v-slot:expand="props">
        <ProjectProposition :item="props.item" :edit-mode="editMode" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { getObjectValueByPath } from "vuetify/lib/util/helpers";
import { mapState } from "vuex";
import { exportPdfVisites, sortPositions } from "../../utils";
import ProjectProposition from "./ProjectProposition";
import ProjectPropositionsRow from "./ProjectPropositionsRow";

export default {
  components: {
    ProjectPropositionsRow,
    ProjectProposition
  },
  props: ["project", "editMode"],
  data: function() {
    return {
      search: "",
      showDisabled: false,
      pagination: {
        sortBy: "visited.dateVisite",
        rowsPerPage: -1
      }
    };
  },
  methods: {
    async exportPDF() {
      await exportPdfVisites(
        this.proposals,
        this.project._id,
        this.project.userId
      );
    },
    customSort(items, index, isDesc) {
      const chosen = this.project.chosenEstate;
      if (index === "position") {
        items.sort(sortPositions(chosen, index, isDesc));
      } else if (index === "visited.dateVisite") {
        items.sort((a, b) => {
          if (isDesc) {
            if (!a.visited) return 1;
            else if (!b.visited) return -1;
            else
              return moment(a.visited.dateVisite).isBefore(b.visited.dateVisite)
                ? -1
                : 1;
          } else {
            if (!a.visited) return -1;
            else if (!b.visited) return 1;
            else
              return moment(a.visited.dateVisite).isBefore(b.visited.dateVisite)
                ? 1
                : -1;
          }
        });
      } else if (index != null) {
        // copypasta from vuetify (not overridable :/)
        return items.sort((a, b) => {
          let sortA = getObjectValueByPath(a, index);
          let sortB = getObjectValueByPath(b, index);

          if (isDesc) {
            [sortA, sortB] = [sortB, sortA];
          }

          // Check if both are numbers
          if (!isNaN(sortA) && !isNaN(sortB)) {
            return sortA - sortB;
          }

          // Check if both cannot be evaluated
          if (sortA === null && sortB === null) {
            return 0;
          }

          [sortA, sortB] = [sortA, sortB].map(s =>
            (s || "").toString().toLocaleLowerCase()
          );

          if (sortA > sortB) return 1;
          if (sortA < sortB) return -1;

          return 0;
        });
      }

      return items;
    }
  },
  computed: {
    ...mapState("projects", {
      allProposals: state => state.proposals
    }),

    proposals() {
      let maxVisits = this.project.chosenEstate ? 2 : 3;
      let prjs = _(this.allProposals)
        .filter(
          ({ visited, visitCancelled }) =>
            visited != null || visitCancelled === true
        )
        .orderBy(["visited.dateVisite"], ["desc"])
        .map(prj => {
          const host = prj._host;
          if (host._id === this.project.chosenEstate) {
            return {
              ...prj,
              signaturePick: true
            };
          }

          if (!maxVisits) {
            return prj;
          }

          const signaturePick =
            host.carrez > 0 &&
            host.price > 0 &&
            typeof host.address === "string" &&
            host.address.length > 0 &&
            typeof host.addressObject === "object" &&
            typeof host.addressObject.route === "string" &&
            host.contactName?.length > 0 &&
            host.contactPhoneNumber?.length > 0;
          if (signaturePick) {
            maxVisits--;
            return {
              ...prj,
              signaturePick
            };
          }

          return prj;
        })
        .value();
      if (this.search !== "") {
        prjs = prjs.filter(({ _host }) => {
          return (
            _host != null &&
            (_host.address.includes(this.search) ||
              (_host.contactName && _host.contactName.includes(this.search)) ||
              _host._id === this.search)
          );
        });
      }
      return prjs
        .filter(({ _host }) => _host && _host.isActive)
        .filter(({ disabled }) => this.showDisabled || !disabled)
        .map(prop => {
          if (prop.position == null) {
            prop.position =
              prop.liked != null ? 99 : prop.disliked != null ? 9999 : 999;
          }
          prop.id = prop._id;
          return prop;
        });
    },
    headers() {
      const h = [
        {
          text: "",
          align: "left",
          sortable: true,
          value: "position",
          width: "10",
          mobile: true
        },
        {
          text: "Créé",
          align: "left",
          sortable: true,
          value: "proposed",
          width: "10"
        },
        {
          text: "Adresse",
          align: "left",
          sortable: true,
          value: "_host.address",
          mobile: true
        },
        {
          text: "Surface",
          align: "left",
          sortable: true,
          value: "_host.carrez",
          width: "10"
        },
        {
          text: "Prix",
          align: "left",
          sortable: true,
          value: "_host.price",
          width: "10"
        },
        {
          text: "Pièces",
          align: "left",
          sortable: true,
          value: "_host.numberOfRoom",
          width: "10"
        },
        {
          text: "Nom",
          align: "left",
          sortable: true,
          value: "_host.contactName",
          width: "100"
        },
        {
          text: "Tel",
          align: "left",
          sortable: true,
          value: "_host.contactPhoneNumber",
          width: "120"
        },
        {
          text: "Date",
          align: "left",
          sortable: true,
          value: "visited.dateVisite",
          width: "10",
          mobile: true
        },
        {
          text: "Doss.",
          align: "left",
          sortable: true,
          value: "",
          width: "10",
          mobile: true
        },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "",
          width: "10",
          mobile: true
        },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "",
          width: "10",
          mobile: true
        }
      ];
      return this.$mq === "lg" ? h : h.filter(col => col.mobile);
    }
  }
};
</script>
<style scoped>
.hints {
  color: grey;
  font-size: 0.9rem;
  margin: 16px 16px 0 16px;
}
</style>
